import axios from 'axios'
import { Message } from 'element-ui'
import store from '../store'
import { getToken } from '@/utils/auth'
import router from './../router'
import { newRequestId, recordClientEvent } from './client-diagnostics'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.BASE_API, // api 的 base_url
  timeout: 30000 // 请求超时时间
})

let sessionCleanupInProgress = false

function forbiddenError (message, status, businessCode, response) {
  const error = new Error(message || '没有权限')
  error.name = 'ForbiddenError'
  error.status = Number(status) || 403
  error.businessCode = Number(businessCode) || 403
  error.isForbidden = true
  error.response = response
  // Existing pages often interpolate the rejection value. Preserve the old
  // readable result while adding machine-readable 403 semantics.
  error.toString = function () { return this.message }
  return error
}

export function isForbiddenError (error) {
  if (!error) return false
  const response = error.response
  const data = response && response.data
  return Boolean(error.isForbidden || Number(error.status) === 403 ||
    Number(response && response.status) === 403 || Number(error.businessCode) === 403 ||
    Number(data && data.code) === 403)
}

// request拦截器
service.interceptors.request.use(
  config => {
    config.headers = config.headers || {}
    config.diagnosticsStarted = Date.now()
    const requestId = newRequestId()
    if (requestId) config.headers['X-Request-ID'] = requestId
    // 路由守卫以 Cookie 作为登录凭据，拦截器必须使用同一数据源。
    // Vuex 仅作为 Cookie 暂时不可用时的兜底，避免两处状态不同步时漏传认证头。
    const token = getToken() || store.getters.token
    if (token) {
      config.headers = config.headers || {}
      config.headers['x-qinglee-auth'] = token
    }

    return config
  },

  error => {
    // Do something with request error
    // console.log(error) // for debug
    return Promise.reject(error)
  }
)

// response 拦截器
service.interceptors.response.use(
  response => {
    const duration = Date.now() - response.config.diagnosticsStarted
    if (duration > 8000) recordClientEvent('slow_request', { duration, status: response.status, requestId: response.headers['x-request-id'] })
    /**
     * code为非200是抛错 可结合自己业务进行修改
     */
    if (response.headers['content-type'] === 'application/octet-stream') {
      return response.data
    }
    const res = response.data
    if (res.code !== 0) {
      /**
       * 20001:token鉴权失败
       * 20002:token已超时
       * 20005:缺少token
       */
      if ((res.code === 20001 || res.code === 20002 || res.code === 20005) && !response.config.skipAuthFailureHandling) {
        forceLogout(res.msg)
      } else if (!response.config.suppressErrorMessage) {
        Message({
          message: res.msg,
          type: 'error',
          duration: 5 * 1000
        })
      }

      if (Number(res.code) === 403) {
        return Promise.reject(forbiddenError(res.msg, 403, res.code, response))
      }
      if (response.config.businessErrorDetails) {
        const error = new Error(res.msg)
        error.businessCode = Number(res.code)
        return Promise.reject(error)
      }
      return Promise.reject(res.msg)
    } else {
      return res.data
    }
  },
  error => {
    const config = error.config || {}
    recordClientEvent('request_error', { duration: Date.now() - config.diagnosticsStarted, status: error.response && error.response.status, requestId: (error.response && error.response.headers && error.response.headers['x-request-id']) || (config.headers && config.headers['X-Request-ID']) })
    const status = error.response && error.response.status
    if (status === 401 && (!error.config || !error.config.skipAuthFailureHandling)) {
      const data = error.response && error.response.data
      forceLogout((data && data.msg) || '登录状态已失效，请重新登录')
    } else if (!error.config || !error.config.suppressErrorMessage) {
      resolveError(error)
    }
    if (status === 403) {
      const data = error.response && error.response.data
      error.isForbidden = true
      error.status = 403
      error.businessCode = Number(data && data.code) || 403
    }
    return Promise.reject(error)
  }
)

function resolveError (err) {
  const data = err.response && err.response.data
  Message({
    message: (data && data.msg) || err.message || String(err),
    type: 'error',
    duration: 5 * 1000
  })
}

export function forceLogout (message) {
  if (sessionCleanupInProgress) return
  sessionCleanupInProgress = true
  store.dispatch('ForceLogout').then(() => {
    if (router.currentRoute.path !== '/login') {
      router.replace({ name: 'Login' })
    }
    Message({
      message: message || '登录状态已失效，请重新登录',
      type: 'warning',
      duration: 5 * 1000
    })
    setTimeout(() => {
      sessionCleanupInProgress = false
    }, 1000)
  }).catch(() => {
    sessionCleanupInProgress = false
  })
}

export default service



// WEBPACK FOOTER //
// ./src/utils/request.js